import { CardContainer } from '../CardContainer';
import type { IncomeCategoryBreakdown } from '../../services/api';

interface Props {
  breakdown: IncomeCategoryBreakdown;
}

export const IncomeCategoryPieChart = ({ breakdown }: Props) => {
  const { lens_total, frame_total, extras_total } = breakdown;
  const total = lens_total + frame_total + extras_total;

  // Format currency
  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('es-GT', {
      style: 'currency',
      currency: 'GTQ',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(val);
  };

  const hasData = total > 0;

  // Calculate percentages and stroke dashes
  // We use a circle with a radius of 15.9155 so the circumference is exactly 100.
  // This makes calculating stroke-dasharray percentages very easy.
  const r = 15.9155;
  const c = 100;

  let lensPct = 0;
  let framePct = 0;
  let extrasPct = 0;

  if (hasData) {
    lensPct = (lens_total / total) * 100;
    framePct = (frame_total / total) * 100;
    extrasPct = (extras_total / total) * 100;
  }

  // To draw slices, we set stroke-dasharray: "length gap".
  // gap is the rest of the circumference (100 - length).
  // We offset each slice by the sum of lengths of previous slices.
  const lensDash = `${lensPct} ${c - lensPct}`;
  const frameDash = `${framePct} ${c - framePct}`;
  const extrasDash = `${extrasPct} ${c - extrasPct}`;

  // Offsets (starting from top, so we shift by -25 or 25 depending on orientation)
  // Our SVG has standard orientation starting from right. We'll rotate the whole svg by -90deg.
  const lensOffset = 0;
  const frameOffset = -lensPct;
  const extrasOffset = -(lensPct + framePct);

  return (
    <CardContainer>
      <div className="flex justify-between items-center mb-6">
        <div className="flex flex-col gap-1">
          <h3 className="text-slate-900 text-lg font-bold">Desglose por Categoría</h3>
          <p className="text-slate-500 text-xs">Contribución al ingreso por tipo de producto (Anual)</p>
        </div>
        <span className="material-symbols-outlined text-slate-400">more_vert</span>
      </div>

      {!hasData ? (
        <div className="relative h-48 w-full flex items-center justify-center bg-slate-50/50 rounded-lg">
          <p className="text-slate-400 text-sm font-medium">Sin datos suficientes</p>
        </div>
      ) : (
        // The fixed height only holds once the donut and legend sit side by side; stacked on
        // mobile the column needs to grow or it overflows the card.
        <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-8 md:h-48">
          <div className="relative w-36 h-36 sm:w-40 sm:h-40 flex-shrink-0">
            <svg viewBox="0 0 36 36" className="w-full h-full transform -rotate-90">
              {/* Background ring */}
              <circle
                cx="18"
                cy="18"
                r={r}
                fill="transparent"
                stroke="#f1f5f9"
                strokeWidth="4"
              />
              {/* Lenses Slice */}
              {lensPct > 0 && (
                <circle
                  cx="18"
                  cy="18"
                  r={r}
                  fill="transparent"
                  stroke="#3b82f6" // blue-500
                  strokeWidth="4"
                  strokeDasharray={lensDash}
                  strokeDashoffset={lensOffset}
                  strokeLinecap="round"
                  className="transition-all duration-1000 ease-out"
                />
              )}
              {/* Frames Slice */}
              {framePct > 0 && (
                <circle
                  cx="18"
                  cy="18"
                  r={r}
                  fill="transparent"
                  stroke="#8b5cf6" // violet-500
                  strokeWidth="4"
                  strokeDasharray={frameDash}
                  strokeDashoffset={frameOffset}
                  strokeLinecap="round"
                  className="transition-all duration-1000 ease-out"
                />
              )}
              {/* Extras Slice */}
              {extrasPct > 0 && (
                <circle
                  cx="18"
                  cy="18"
                  r={r}
                  fill="transparent"
                  stroke="#f59e0b" // amber-500
                  strokeWidth="4"
                  strokeDasharray={extrasDash}
                  strokeDashoffset={extrasOffset}
                  strokeLinecap="round"
                  className="transition-all duration-1000 ease-out"
                />
              )}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-2xl font-black text-slate-800 tracking-tight">100%</span>
              <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Total</span>
            </div>
          </div>

          <div className="flex flex-col gap-3 w-full md:w-auto min-w-[140px]">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                <span className="text-sm font-semibold text-slate-700">Lentes</span>
              </div>
              <div className="text-right">
                <span className="text-sm font-bold text-slate-900">{formatCurrency(lens_total)}</span>
              </div>
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-violet-500"></span>
                <span className="text-sm font-semibold text-slate-700">Aros</span>
              </div>
              <div className="text-right">
                <span className="text-sm font-bold text-slate-900">{formatCurrency(frame_total)}</span>
              </div>
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                <span className="text-sm font-semibold text-slate-700">Extras</span>
              </div>
              <div className="text-right">
                <span className="text-sm font-bold text-slate-900">{formatCurrency(extras_total)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </CardContainer>
  );
};
